/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, lazy, startTransition, Suspense, useState, useEffect } from 'react';
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { LTPromo } from '../Promo';
import { updateAEMCQKeys } from '@marriott/mi-ui-library';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { LocationsSectionProps, PublishCardWrapperProps } from './LTLocations.types';
import { StyledLocations } from './LTLocations.styles';

export const LocationsSectionConfig = {
  emptyLabel: 'LoveTravelsLocations',
  isEmpty: () => true,
};

const localModules: Record<string, () => Promise<any>> = {
  ExploreLocations: () => import('../ExploreLocations'),
  Media: () => import('@marriott/mi-ui-library'),
  MiniCardCarouselContainer: () => import('../MiniCardCarouselContainer'),
};

const FallbackComponent: React.FC = () => null;

const getComponent = (moduleName: string) => {
  return lazy(async () => {
    const module = await localModules[moduleName]();
    const Component = module[moduleName] || module.default;
    return { default: Component ?? FallbackComponent };
  });
};

const mapper: Record<string, string> = {};
mapper['explorelocations'] = 'ExploreLocations';
mapper['media'] = 'Media';
mapper['cardminicarouselcontainer'] = 'MiniCardCarouselContainer';

const PublishCardWrapper: FC<PublishCardWrapperProps> = (props: PublishCardWrapperProps) => {
  const { containerName, jsonData } = props;
  const [Component, setComponent] = useState<React.LazyExoticComponent<any> | null>(null);
  const [componentProps, setComponentProps] = useState<any>(null);

  useEffect(() => {
    if (jsonData && Object.prototype.hasOwnProperty.call(jsonData, containerName)) {
      const comp = jsonData[containerName];
      const compItems = comp[':items'];

      for (const itemKey in compItems) {
        if (Object.prototype.hasOwnProperty.call(compItems, itemKey)) {
          const item = compItems[itemKey];
          const itemType = item[':type']?.split('/').pop();
          const items = item[':items'];

          if (Object.prototype.hasOwnProperty.call(mapper, itemType)) {
            const innerComp = mapper[itemType];

            // ✅ Defer component creation until after hydration
            startTransition(() => {
              setComponent(getComponent(innerComp));
              setComponentProps({
                ...item,
                defaultCardsPerView: 3,
                cqItems: items,
                additionalWrapperClass: '',
              });
            });

            return; // Exit loop after scheduling update
          }
        }
      }
    }
  }, [containerName, jsonData]);

  if (!Component || !componentProps) return null;

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Component {...componentProps} />
    </Suspense>
  );
};

export const LocationsSection: React.FC<LocationsSectionProps> = ({
  title = 'Top Supporting Hotels',
  cqItems = {},
  ...props
}) => {
  const isAuthorMode = AuthoringUtils.isInEditor();
  const updatedProps = updateAEMCQKeys(props);
  const cqPath = updatedProps['cqPath'];
  const parts = cqPath.split('/jcr:content');
  const pagePath = parts[0];
  const itemPath = parts[1];

  const authorComponent = (compName: string) => {
    return cqPath ? (
      <ResponsiveGrid
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${compName}`}
        columnCount="12"
        gridClassNames={''}
        config={{
          isEmpty: () => true,
          resourceType: 'mi-aem-martech/components/container',
        }}
        isInEditor={true}
      />
    ) : null;
  };

  return (
    <StyledLocations className="lt-locations lt-unicef-locations">
      <div className="container p-sm-0">
        {isAuthorMode && authorComponent('media-container')}
        <div className="row lt-gif">
          {!isAuthorMode && <PublishCardWrapper containerName={'media-container'} jsonData={cqItems} />}
        </div>

        <div>
          <h2 className="lt-kicker-2">{title}</h2>
          {isAuthorMode && authorComponent('lt-links')}
          <div className="row lt-links">
            {!isAuthorMode && <PublishCardWrapper containerName={'lt-links'} jsonData={cqItems} />}
          </div>
        </div>
        {isAuthorMode && authorComponent('locations-container')}

        {!isAuthorMode && <PublishCardWrapper containerName={'locations-container'} jsonData={cqItems} />}
      </div>
    </StyledLocations>
  );
};

export const LTLocations = (props: LocationsSectionProps) => {
  return (
    <div data-testid="locations" data-component-name="o-prearrival-lovetravels-locations">
      <EditableComponent config={LocationsSectionConfig} {...props}>
        <LocationsSection {...props} />
      </EditableComponent>
    </div>
  );
};
